import { useMemo } from "react";
import { create } from "zustand";
import {
  useSpecificationStateStore,
  makeAttributeChangeTarget,
  isItem,
  type Change,
  type ChangeTarget,
} from "./specificationState";
import {
  findConstraintOfType,
  getItemAtPath,
  parseBomPath,
} from "../../utils/conditions";

// ============================================================================
// Pure Utility Functions (no store access)
// ============================================================================

/**
 * Build a translator from attribute UUID to attribute name and item UUID
 * This is needed because formula variables reference attribute UUIDs, but we track changes by item + name
 * This must be built from actual item data (with item_type info), not just constraints
 */
export const buildAttributeUuidTranslator = (
  item: ItemWithBOM | null
): AttributeUuidTranslator => {
  const translator: AttributeUuidTranslator = new Map();

  if (!item) {
    return translator;
  }

  // Recursively process the item tree to map attribute UUIDs
  const processItem = (itemWithBOM: ItemWithBOM, path: string) => {
    const currentItem = itemWithBOM.item;

    // Only process if it's an Item (has uuid and item_type)
    if ("uuid" in currentItem && "item_type" in currentItem) {
      const typedItem = currentItem as Item;

      // Map each attribute's UUID to all locations (multiple instances
      // of the same template share attribute UUIDs but live at different paths)
      if (typedItem.item_type?.attributes) {
        typedItem.item_type.attributes.forEach((attribute) => {
          const locations = translator.get(attribute.uuid) || [];
          locations.push({
            itemUuid: typedItem.uuid,
            attributeName: attribute.name,
            path: path,
          });
          translator.set(attribute.uuid, locations);
        });
      }
    }

    // Recursively process BOM children
    if (itemWithBOM.bom) {
      itemWithBOM.bom.forEach((bomItem: ItemWithBOM, index: number) => {
        const childPath = path ? `${path}>${index}` : `${index}`;
        processItem(bomItem, childPath);
      });
    }
  };

  processItem(item, "");
  return translator;
};

/**
 * Build formula dependency map from ConstraintsResponse
 * Keys are serialized ChangeTargets (e.g., "path:propertyName")
 * Requires an attribute UUID translator to resolve variable references
 *
 * When multiple instances of the same template exist in the BOM tree
 * (sharing the same attribute UUIDs but at different paths), each instance
 * gets its own set of dependency entries so that changing a source attribute
 * on one instance only updates the target on that same instance.
 */
export const buildFormulaDependencyMap = (
  constraintsResponse: ConstraintsResponse | undefined,
  attributeUuidTranslator: AttributeUuidTranslator
): FormulaDependencyMap => {
  const dependencyMap: FormulaDependencyMap = {};

  if (!constraintsResponse) {
    return dependencyMap;
  }

  // Iterate through all item constraints to find formula constraints
  constraintsResponse.item_constraints.forEach(
    (itemConstraint: ItemConstraints) => {
      // Check each attribute constraint for formula constraints
      Object.values(itemConstraint.attribute_constraints ?? {})
        .flat()
        .forEach((constraint: TemplateConstraintModel) => {
          if (constraint.constraint_type === "attribute_formula") {
            const formulaConstraint = constraint as AttributeFormulaConstraint;

            // Find ALL target locations using the target_attribute_uuid
            const targetLocations = attributeUuidTranslator.get(
              formulaConstraint.target_attribute_uuid
            );

            if (!targetLocations || targetLocations.length === 0) {
              return;
            }

            // For each target instance, build dependencies scoped to that
            // instance's path. This ensures that a change to anode A's
            // source attribute only updates anode A's target, not anode B's.
            targetLocations.forEach((targetLocation) => {
              formulaConstraint.config.variables.forEach(
                (variable: FormulaVariable) => {
                  const sourceLocations = attributeUuidTranslator.get(
                    variable.attribute_uuid
                  );

                  if (!sourceLocations || sourceLocations.length === 0) {
                    return;
                  }

                  // Find the source location that belongs to the same
                  // instance as the target. Two attributes belong to the
                  // same instance when they share the same bom_path.
                  const coLocatedSource = sourceLocations.find(
                    (s) => s.path === targetLocation.path
                  );

                  if (!coLocatedSource) {
                    return;
                  }

                  const sourceKey = `${coLocatedSource.path}:${coLocatedSource.attributeName}`;

                  if (!dependencyMap[sourceKey]) {
                    dependencyMap[sourceKey] = [];
                  }

                  // De-duplicate: avoid adding the same target dependency twice
                  const isDuplicate = dependencyMap[sourceKey].some(
                    (d) =>
                      d.targetPath === targetLocation.path &&
                      d.targetAttributeName === targetLocation.attributeName &&
                      d.formula === formulaConstraint.config.expression
                  );

                  if (!isDuplicate) {
                    dependencyMap[sourceKey].push({
                      targetAttributeName: targetLocation.attributeName,
                      targetItemUuid: targetLocation.itemUuid,
                      targetPath: targetLocation.path,
                      formula: formulaConstraint.config.expression,
                      variables: formulaConstraint.config.variables,
                    });
                  }
                }
              );
            });
          }
        });
    }
  );

  return dependencyMap;
};

/**
 * Extract template UUID from constraint (handles different field names)
 */
export const getConstraintTemplateUuid = (constraint: any): string | null => {
  const fields = [
    "required_template_uuid",
    "target_template_uuid",
    "template_uuid",
    "process_template_uuid",
  ];
  for (const field of fields) {
    if (constraint[field] && typeof constraint[field] === "string") {
      return constraint[field];
    }
  }
  return null;
};

/**
 * Extract item type UUID from constraint
 */
export const getConstraintItemTypeUuid = (constraint: any): string | null => {
  const fields = [
    "item_type_uuid",
    "required_item_type_uuid",
    "target_item_type_uuid",
  ];
  for (const field of fields) {
    if (constraint[field] && typeof constraint[field] === "string") {
      return constraint[field];
    }
  }
  return null;
};

/**
 * Get every constraint targeting a specific item attribute
 */
export const getAttributeConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string,
  attributeName: string
): TemplateConstraintModel[] => {
  if (!constraints) return [];

  const itemConstraints = constraints.item_constraints.find(
    (ic) => ic.item_uuid === itemUuid
  );
  if (!itemConstraints) return [];

  return itemConstraints.attribute_constraints?.[attributeName] ?? [];
};

/**
 * Get every constraint targeting a specific process parameter
 */
export const getParameterConstraints = (
  constraints: ConstraintsResponse | null,
  processUuid: string,
  parameterName: string
): TemplateConstraintModel[] => {
  if (!constraints) return [];

  const processConstraints = constraints.process_constraints.find(
    (pc) => pc.process_uuid === processUuid
  );
  if (!processConstraints) return [];

  return processConstraints.parameter_constraints?.[parameterName] ?? [];
};

/**
 * PROCESS-target constraints from a process's own template. These govern the
 * process's steps (e.g. `allowed_process_templates` for sub-processes).
 */
export const getProcessOwnConstraints = (
  constraints: ConstraintsResponse | null,
  processUuid: string | null | undefined
): TemplateConstraintModel[] => {
  if (!constraints || !processUuid) return [];
  return (
    constraints.process_constraints.find(
      (pc) => pc.process_uuid === processUuid
    )?.process_constraints ?? []
  );
};

export interface LiveProcessEntry {
  processItem: ProcessWithSteps;
  /** Indices from the item's processes down through `steps`. */
  processPath: number[];
}

/**
 * Flatten an item's non-deleted process tree (top-level processes and their
 * steps, up to two levels deep) in tree order, so nested processes are
 * validated and reported like top-level ones.
 */
export const collectLiveProcesses = (
  item: Pick<ItemWithBOM, "processes" | "process_changes">
): LiveProcessEntry[] => {
  const result: LiveProcessEntry[] = [];
  const deletedIndices = (
    changes?: { index: number; is_deleted?: boolean }[]
  ) => new Set((changes || []).filter((c) => c.is_deleted).map((c) => c.index));
  const visit = (
    entries: ProcessWithSteps[],
    parentPath: number[],
    deleted: Set<number>
  ) => {
    entries.forEach((processItem, index) => {
      if (deleted.has(index)) return;
      const processPath = [...parentPath, index];
      result.push({ processItem, processPath });
      if (processPath.length <= 2) {
        visit(
          processItem.steps || [],
          processPath,
          deletedIndices(processItem.step_changes)
        );
      }
    });
  };
  visit(item.processes || [], [], deletedIndices(item.process_changes));
  return result;
};

/**
 * Template UUIDs that may be added as a child process, given the parent's
 * PROCESS constraints (an item's `process_constraints`, or a parent process's
 * own `process_constraints` for steps). Templates whose
 * `required_process_template.max_quantity` is already reached among the
 * non-deleted `existing` children are excluded.
 */
export const getAllowedProcessTemplateUuids = (
  processConstraints: TemplateConstraintModel[],
  existing: ProcessWithSteps[],
  isDeleted: (index: number) => boolean
): string[] => {
  const templateUuids = new Set<string>();
  processConstraints
    .filter(
      (c): c is AllowedProcessTemplatesConstraint =>
        c.constraint_type === "allowed_process_templates"
    )
    .forEach((c) =>
      c.config.allowed_template_uuids?.forEach((uuid) =>
        templateUuids.add(uuid)
      )
    );

  const maxConstraints = processConstraints.filter(
    (c): c is RequiredProcessTemplateConstraint =>
      c.constraint_type === "required_process_template"
  );
  if (maxConstraints.length === 0) {
    return Array.from(templateUuids);
  }

  const templateCounts = new Map<string, number>();
  existing.forEach((entry, index) => {
    if (isDeleted(index)) return;
    const templateUuid = entry.process.base_template?.template_definition_uuid;
    if (templateUuid) {
      templateCounts.set(
        templateUuid,
        (templateCounts.get(templateUuid) || 0) + 1
      );
    }
  });

  return Array.from(templateUuids).filter((templateUuid) =>
    maxConstraints.every((constraint) => {
      const max = constraint.config.max_quantity;
      if (
        getConstraintTemplateUuid(constraint) !== templateUuid ||
        max === undefined ||
        max === null
      ) {
        return true;
      }
      return (templateCounts.get(templateUuid) || 0) < max;
    })
  );
};

/**
 * Get BOM constraints for a specific item
 */
export const getBomConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): TemplateConstraintModel[] => {
  if (!constraints) return [];

  const itemConstraints = constraints.item_constraints.find(
    (ic) => ic.item_uuid === itemUuid
  );
  if (!itemConstraints) return [];

  return itemConstraints.bom_constraints || [];
};

/**
 * Get BOM constraints filtered by constraint type
 */
export const getBomConstraintsByType = (
  constraints: ConstraintsResponse | null,
  itemUuid: string,
  constraintType: string
): TemplateConstraintModel[] => {
  const bomConstraints = getBomConstraints(constraints, itemUuid);
  return bomConstraints.filter((c) => c.constraint_type === constraintType);
};

/**
 * Get allowed BOM template UUIDs for a specific item
 * Extracts template UUIDs from all "allowed_bom_templates" constraints
 */
export const getAllowedBomTemplateUuids = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): string[] => {
  const allowedBomTemplatesConstraints = getBomConstraintsByType(
    constraints,
    itemUuid,
    "allowed_bom_templates"
  );

  const templateUuids = new Set<string>();
  allowedBomTemplatesConstraints.forEach((constraint) => {
    const allowedUuids = (constraint as any).config?.allowed_template_uuids;
    if (Array.isArray(allowedUuids)) {
      allowedUuids.forEach((uuid: string) => templateUuids.add(uuid));
    }
  });

  return Array.from(templateUuids);
};

/**
 * Get required BOM item type constraints for a specific item
 */
export const getRequiredBomItemTypeConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): TemplateConstraintModel[] => {
  return getBomConstraintsByType(
    constraints,
    itemUuid,
    "required_bom_item_type"
  );
};

/**
 * Get required BOM template constraints for a specific item
 */
export const getRequiredBomTemplateConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): TemplateConstraintModel[] => {
  return getBomConstraintsByType(
    constraints,
    itemUuid,
    "required_bom_template"
  );
};

/**
 * Get aggregate limit constraints for a specific item
 */
export const getAggregateLimitConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): TemplateConstraintModel[] => {
  return getBomConstraintsByType(constraints, itemUuid, "aggregate_limit");
};

export const getAllowedBomTemplatesConstraints = (
  constraints: ConstraintsResponse | null,
  itemUuid: string
): TemplateConstraintModel[] => {
  return getBomConstraintsByType(
    constraints,
    itemUuid,
    "allowed_bom_templates"
  );
};

export const validateRequiredBomItemTypes = (
  constraints: ConstraintsResponse | null,
  parentItemUuid: string | null,
  currentPath: string,
  allBomItems: Array<{ child: BOMItem; originalIndex: number }>,
  isBomItemDeleted: (index: number) => boolean,
  getChange: (target: any) => any | null
): void => {
  if (!constraints || !parentItemUuid) return;

  const requiredBomItemTypeConstraints = getRequiredBomItemTypeConstraints(
    constraints,
    parentItemUuid
  );

  if (requiredBomItemTypeConstraints.length === 0) {
    return;
  }

  const { childCountsByItemType } = buildChildCountMaps(
    allBomItems.map(({ child }) => child),
    currentPath,
    isBomItemDeleted,
    getChange
  );

  requiredBomItemTypeConstraints.forEach((constraint) => {
    const config = (constraint as any).config;
    const constraintUuid = constraint.uuid;
    const targetItemTypeUuid = (constraint as any).target_item_type_uuid;
    const minQty = config?.min_quantity;
    const maxQty = config?.max_quantity;

    if (!targetItemTypeUuid) {
      return;
    }

    const totalQuantity = childCountsByItemType.get(targetItemTypeUuid) || 0;

    const violatesMin =
      minQty !== undefined && minQty !== null && totalQuantity < minQty;
    const violatesMax =
      maxQty !== undefined && maxQty !== null && totalQuantity > maxQty;

    if (violatesMin || violatesMax) {
      upsertViolation({
        constraint_uuid: constraintUuid,
        constraint_type: "required_bom_item_type",
        bom_path: currentPath,
        item_uuid: parentItemUuid,
        message: `BOM must include between ${minQty ?? 0} and ${
          maxQty ?? "∞"
        } of required item type`,
      });
    } else {
      removeViolation("required_bom_item_type", currentPath, parentItemUuid);
    }
  });
};

/**
 * Generates a unique key for a constraint violation based on constraint_type,
 * item_uuid, and bom_path. This ensures that for the same constraint type
 * applied to the same item at the same BOM path, only one violation exists.
 */
export const getViolationKey = (v: {
  constraint_type: string | null;
  item_uuid?: string | null;
  bom_path?: string | null;
}): string => {
  return `${v.constraint_type}:${v.item_uuid || ""}:${v.bom_path || ""}`;
};

/**
 * Remaps a server-returned bom_path from post-deletion indices back to
 * pre-deletion indices so that violations align with the frontend tree
 * (which still includes pending-deleted items at their original positions).
 *
 * The server applies deletions before validating, so its indices skip deleted
 * items. For example, if the frontend tree has items [A(deleted), B, C] at
 * some level, the server sees [B, C] with indices 0 and 1. This function
 * converts server index 0 back to frontend index 1, and server index 1 back
 * to frontend index 2.
 */
export const remapServerBomPath = (
  serverBomPath: string,
  condition: ItemWithBOM
): string => {
  if (!serverBomPath) return serverBomPath;

  const serverIndices = parseBomPath(serverBomPath);
  const remappedSegments: number[] = [];

  let currentItem: ItemWithBOM = condition;

  for (const serverIndex of serverIndices) {
    const deletedIndices = new Set(
      (currentItem.bom_changes || [])
        .filter((c) => c.is_deleted && !c.is_added)
        .map((c) => c.index)
    );

    // Walk the frontend bom array, skipping deleted items, to find the
    // frontend index that corresponds to the server's non-deleted index.
    let nonDeletedCount = 0;
    let frontendIndex = -1;

    for (let i = 0; i < (currentItem.bom || []).length; i++) {
      if (deletedIndices.has(i)) {
        continue;
      }
      if (nonDeletedCount === serverIndex) {
        frontendIndex = i;
        break;
      }
      nonDeletedCount++;
    }

    if (frontendIndex === -1) {
      // Server index is out of range; return the original path unchanged
      return serverBomPath;
    }

    remappedSegments.push(frontendIndex);
    currentItem = currentItem.bom[frontendIndex];
  }

  return remappedSegments.join(">");
};

// ============================================================================
// BOM Constraint Helper Functions
// ============================================================================

/**
 * Helper to get the current quantity for a BOM item, considering tracked changes
 */
export const getCurrentQuantity = (
  child: BOMItem,
  originalIndex: number,
  currentPath: string,
  validationIndex: number | undefined,
  validationQuantity: number | undefined,
  getChange: (target: any) => any | null
): number => {
  // If this is the item being validated, use the parameter quantity
  if (originalIndex === validationIndex && validationQuantity !== undefined) {
    return validationQuantity;
  }

  // Otherwise, check for tracked changes
  const changeTarget = {
    type: "bom_quantity" as const,
    path: currentPath,
    bomIndex: originalIndex,
  };
  const change = getChange(changeTarget);

  // Use tracked quantity if available, otherwise use original quantity
  return change && change.currentValue !== undefined
    ? (change.currentValue as number)
    : child.quantity;
};

/**
 * Build maps of child counts by template UUID and item type UUID
 * Accounts for deleted items and quantity changes
 */
export const buildChildCountMaps = (
  bomItems: BOMItem[],
  currentPath: string,
  isBomItemDeleted: (index: number) => boolean,
  getChange: (target: any) => any | null
): {
  childCountsByTemplate: Map<string, number>;
  childCountsByItemType: Map<string, number>;
} => {
  const childCountsByTemplate = new Map<string, number>();
  const childCountsByItemType = new Map<string, number>();

  bomItems.forEach((bomItem, idx) => {
    // Skip if not an Item or if deleted
    if (!("uuid" in bomItem.item && "item_type" in bomItem.item)) return;
    if (isBomItemDeleted(idx)) return;

    const item = bomItem.item as Item;

    // Get the current quantity (accounting for tracked changes)
    const changeTarget = {
      type: "bom_quantity" as const,
      path: currentPath,
      bomIndex: idx,
    };
    const change = getChange(changeTarget);
    const currentQuantity =
      change && change.currentValue !== undefined
        ? (change.currentValue as number)
        : bomItem.quantity;

    const templateUuid = item.base_template?.template_definition_uuid;
    const itemTypeUuid = item.item_type?.uuid;

    if (templateUuid) {
      childCountsByTemplate.set(
        templateUuid,
        (childCountsByTemplate.get(templateUuid) || 0) + currentQuantity
      );
    }
    if (itemTypeUuid) {
      childCountsByItemType.set(
        itemTypeUuid,
        (childCountsByItemType.get(itemTypeUuid) || 0) + currentQuantity
      );
    }
  });

  return { childCountsByTemplate, childCountsByItemType };
};

/**
 * Checks if a BOM item can be deleted without violating min_quantity constraints
 */
export const canDeleteBomItem = (
  bomItem: BOMItem,
  requiredBomTemplateConstraints: any[],
  requiredBomItemTypeConstraints: any[],
  childCountsByTemplate: Map<string, number>,
  childCountsByItemType: Map<string, number>
): { canDelete: boolean; reason?: string } => {
  // Only Items can be checked against constraints
  if (!("uuid" in bomItem.item && "item_type" in bomItem.item)) {
    return { canDelete: true };
  }

  const childItem = bomItem.item as Item;
  const templateUuid = childItem.base_template?.template_definition_uuid;
  const itemTypeUuid = childItem.item_type?.uuid;

  // Check RequiredBOMTemplateConstraint min_quantity
  if (templateUuid) {
    const templateConstraint = requiredBomTemplateConstraints.find(
      (c: any) => c.target_template_uuid === templateUuid
    );
    if (templateConstraint) {
      const minQty = (templateConstraint as any).config?.min_quantity;
      if (minQty !== undefined && minQty !== null && minQty > 0) {
        const currentCount = childCountsByTemplate.get(templateUuid) || 0;
        const newCount = currentCount - bomItem.quantity;
        if (newCount < minQty) {
          return {
            canDelete: false,
            reason: `Cannot delete: minimum quantity of ${minQty} required for this template`,
          };
        }
      }
    }
  }

  // Check RequiredBOMItemTypeConstraint min_quantity
  if (itemTypeUuid) {
    const itemTypeConstraint = requiredBomItemTypeConstraints.find(
      (c: any) => c.target_item_type_uuid === itemTypeUuid
    );
    if (itemTypeConstraint) {
      const minQty = (itemTypeConstraint as any).config?.min_quantity;
      if (minQty !== undefined && minQty !== null && minQty > 0) {
        const currentCount = childCountsByItemType.get(itemTypeUuid) || 0;
        const newCount = currentCount - bomItem.quantity;
        if (newCount < minQty) {
          return {
            canDelete: false,
            reason: `Cannot delete: minimum quantity of ${minQty} required for this item type`,
          };
        }
      }
    }
  }

  return { canDelete: true };
};

/**
 * Helper to validate min/max quantity constraints
 */
export const validateMinMaxConstraint = (
  quantity: number | undefined,
  constraintUuid: string,
  minQty: number | undefined | null,
  maxQty: number | undefined | null,
  itemName: string,
  constraintType: "required_bom_template" | "required_bom_item_type",
  violationPath: string,
  itemUuid: string | null
): void => {
  // If quantity is undefined or null, remove any existing violation
  if (quantity === undefined || quantity === null) {
    removeViolation(constraintType, violationPath, itemUuid);
    return;
  }

  // Check if quantity violates min/max constraints
  const violatesMin =
    minQty !== undefined && minQty !== null && quantity < minQty;
  const violatesMax =
    maxQty !== undefined && maxQty !== null && quantity > maxQty;

  if (violatesMin || violatesMax) {
    upsertViolation({
      constraint_uuid: constraintUuid,
      constraint_type: constraintType as TemplateConstraintType,
      bom_path: violationPath,
      item_uuid: itemUuid,
      message: `${itemName} quantity must be between ${minQty ?? 0} and ${
        maxQty ?? "∞"
      }`,
    });
  } else {
    removeViolation(constraintType, violationPath, itemUuid);
  }
};

/**
 * Validates BOM item quantity against constraints and upserts/removes violations accordingly.
 *
 * This function checks two types of constraints:
 * 1. required_bom_item_type - min/max quantity for specific item types
 * 2. aggregate_limit - max total quantity across multiple items matching field identifiers
 *
 * Note: required_bom_template constraints are validated separately by validateRequiredBomTemplates
 * as aggregate checks across all items, not per-item checks.
 *
 * For aggregate limits, it uses tracked quantity changes for ALL items in the BOM,
 * not just the item being validated, ensuring violations reflect pending changes.
 */
export const validateBomItemQuantity = (
  quantity: number | undefined,
  item: Item,
  currentPath: string,
  index: number,
  constraints: ConstraintsResponse | null,
  parentItemUuid: string | null,
  allBomItems: Array<{ child: BOMItem; originalIndex: number }>,
  isBomItemDeleted: (index: number) => boolean,
  getChange: (target: any) => any | null
): void => {
  if (!constraints || !parentItemUuid) return;

  const itemTypeUuid = item.item_type?.uuid;
  const violationPath = currentPath;

  // Check RequiredBOMItemTypeConstraint (per-item validation)
  // Note: RequiredBOMTemplateConstraint is handled by validateRequiredBomTemplates as an aggregate
  if (itemTypeUuid) {
    const requiredBomItemTypeConstraints = getRequiredBomItemTypeConstraints(
      constraints,
      parentItemUuid
    );

    const itemTypeConstraint = requiredBomItemTypeConstraints.find(
      (c: any) => c.target_item_type_uuid === itemTypeUuid
    );

    if (itemTypeConstraint) {
      const config = (itemTypeConstraint as any).config;
      validateMinMaxConstraint(
        quantity,
        config.uuid,
        config?.min_quantity,
        config?.max_quantity,
        item.item_type?.name || "Unknown Item Type",
        "required_bom_item_type",
        violationPath,
        parentItemUuid
      );
    }
  }

  // Check AggregateLimitConstraint for all BOM items
  const aggregateLimitConstraints = getAggregateLimitConstraints(
    constraints,
    parentItemUuid
  );

  for (const constraint of aggregateLimitConstraints) {
    const config = (constraint as any).config;
    const constraintUuid = config.uuid;
    const fieldIdentifiers = config?.field_identifiers || [];
    const maxTotal = config?.max_total;

    if (maxTotal === undefined || maxTotal === null) continue;

    // Calculate current total across all items matching the field_identifiers
    let currentTotal = 0;
    const affectedBomIndices: number[] = [];
    const affectedItemNames: string[] = [];

    allBomItems.forEach(({ child, originalIndex }) => {
      if (isBomItemDeleted(originalIndex)) return;
      if (!("uuid" in child.item && "item_type" in child.item)) return;

      const childItem = child.item as Item;
      const childTemplateUuid =
        childItem.base_template?.template_definition_uuid;
      const childItemTypeUuid = childItem.item_type?.uuid;
      const childItemTypeName = childItem.item_type?.name;

      // Check if this child matches any field_identifier
      // Normalize to lowercase for case-insensitive comparison
      const childIdentifiers = [
        childTemplateUuid?.toLowerCase(),
        childItemTypeUuid?.toLowerCase(),
        childItemTypeName?.toLowerCase(),
      ].filter((id): id is string => !!id);

      // Also normalize field identifiers to lowercase
      const normalizedFieldIdentifiers = fieldIdentifiers.map((id: string) =>
        id.toLowerCase()
      );

      const matches = childIdentifiers.some((id) =>
        normalizedFieldIdentifiers.includes(id)
      );

      if (matches) {
        const itemQuantity = getCurrentQuantity(
          child,
          originalIndex,
          currentPath,
          index,
          quantity,
          getChange
        );

        currentTotal += itemQuantity;
        affectedBomIndices.push(originalIndex);

        // Track affected item names for better error message
        const itemName =
          childItemTypeName || childItem.arena_part_number || "Unknown item";
        if (!affectedItemNames.includes(itemName)) {
          affectedItemNames.push(itemName);
        }
      }
    });

    // Update violations for all affected items
    const violates = currentTotal > maxTotal;

    // For aggregate limits, create one violation on the parent item (not on each child)
    // since this is a constraint on the parent's BOM composition
    if (violates) {
      // Build a detailed, helpful error message
      const itemTypesList =
        affectedItemNames.length > 0
          ? affectedItemNames.join(", ")
          : "matching items";
      const identifiersList =
        fieldIdentifiers.length > 0 ? ` (${fieldIdentifiers.join(", ")})` : "";

      const message = `Aggregate limit exceeded: Total quantity of ${currentTotal} across ${affectedBomIndices.length} ${itemTypesList}${identifiersList} exceeds maximum of ${maxTotal}`;

      upsertViolation({
        constraint_uuid: constraintUuid,
        constraint_type: "aggregate_limit",
        bom_path: currentPath,
        item_uuid: parentItemUuid,
        message: message,
      });
    } else {
      removeViolation("aggregate_limit", currentPath, parentItemUuid);
    }
  }
};

/**
 * Validates that all required BOM templates are present in the BOM with sufficient quantity
 */
export const validateRequiredBomTemplates = (
  constraints: ConstraintsResponse | null,
  parentItemUuid: string | null,
  currentPath: string,
  allBomItems: Array<{ child: BOMItem; originalIndex: number }>,
  isBomItemDeleted: (index: number) => boolean,
  getChange: (target: any) => any | null
): void => {
  if (!constraints || !parentItemUuid) return;

  const requiredBomTemplateConstraints = getRequiredBomTemplateConstraints(
    constraints,
    parentItemUuid
  );

  for (const constraint of requiredBomTemplateConstraints) {
    const config = (constraint as any).config;
    const constraintUuid = config.uuid;
    const targetTemplateUuid = (constraint as any).target_template_uuid;
    const minQty = config?.min_quantity;

    if (!targetTemplateUuid || minQty === undefined || minQty === null) {
      continue;
    }

    // Calculate total quantity of items matching this template
    let totalQuantity = 0;
    let templateName = "Unknown Template";

    allBomItems.forEach(({ child, originalIndex }) => {
      if (isBomItemDeleted(originalIndex)) return;
      if (!("uuid" in child.item && "item_type" in child.item)) return;

      const childTemplateUuid = (child.item as Item).base_template
        ?.template_definition_uuid;

      // Check if this child matches the required template
      if (childTemplateUuid === targetTemplateUuid) {
        // Get current quantity including tracked changes
        const currentQuantity = getCurrentQuantity(
          child,
          originalIndex,
          currentPath,
          undefined,
          undefined,
          getChange
        );
        totalQuantity += currentQuantity;

        // Store template name for error message
        const childItem = child.item as Item;
        if (childItem.base_template?.name) {
          templateName = childItem.base_template.name;
        }
      }
    });

    // Check if the minimum quantity requirement is met
    if (totalQuantity < minQty) {
      upsertViolation({
        constraint_uuid: constraintUuid,
        constraint_type: "required_bom_template",
        bom_path: currentPath,
        item_uuid: parentItemUuid,
        message: `BOM must include at least ${minQty} of template ${templateName}`,
      });
    } else {
      removeViolation("required_bom_template", currentPath, parentItemUuid);
    }
  }
};

// ============================================================================
// Constraint Violation Summary Types
// ============================================================================

export type ConstraintContextScope =
  | "attribute"
  | "parameter"
  | "item"
  | "bom"
  | "process_requirement"
  | "unknown";

export type ConstraintContext = {
  scope: ConstraintContextScope;
  constraint: TemplateConstraintModel;
  itemUuid?: string | null;
  attributeName?: string | null;
  attributeUuid?: string | null;
  processUuid?: string | null;
  processTypeUuid?: string | null;
  parameterName?: string | null;
  parameterUuid?: string | null;
  templateUuid?: string | null;
};

type ConstraintViolationOptionalFields = {
  item_uuid?: string | null;
  item_part_number?: string | null;
  attribute_uuid?: string | null;
  attribute_name?: string | null;
  process_uuid?: string | null;
  process_type_uuid?: string | null;
  parameter_uuid?: string | null;
  parameter_name?: string | null;
  item_type_uuid?: string | null;
  bom_path?: string | null; // Position-based path like "0>1>2" for unique identification
  bom_index?: number | null; // Index within parent's BOM array
};

export type ConstraintViolationWithContext = ConstraintViolation &
  ConstraintViolationOptionalFields & {
    context: ConstraintContext | null;
  };

export type ConstraintViolationHelpers = {
  // Path-based lookups for reliable item identification
  getItemViolationsByPath: (
    bomPath: string
  ) => ConstraintViolationWithContext[];

  getAttributeViolationsByPath: (
    bomPath: string,
    attributeName: string | null | undefined
  ) => ConstraintViolationWithContext[];

  getProcessViolationsByPath: (
    bomPath: string,
    processIndex: number,
    processUuid?: string | null
  ) => ConstraintViolationWithContext[];

  getParameterViolationsByPath: (
    bomPath: string,
    /** Top-level process index, or -1 for a step (matched by uuid only). */
    processIndex: number,
    parameterName: string | null | undefined,
    processUuid?: string | null
  ) => ConstraintViolationWithContext[];

  /** Returns true if any violation exists at `bomPath` or any descendant path. */
  hasViolationsAtOrBelow: (bomPath: string) => boolean;
};

export type ConstraintViolationSummaryResult = {
  list: ConstraintViolationWithContext[];
  hasViolations: boolean;
  contextByConstraintId: Record<string, ConstraintContext>;
  violationsByConstraintId: Record<string, ConstraintViolationWithContext[]>;
} & ConstraintViolationHelpers;

const trimValue = (value?: string | null): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const normalizeKey = (value?: string | null): string | null => {
  const trimmed = trimValue(value);
  return trimmed ? trimmed.toLowerCase() : null;
};

const getConstraintString = (
  constraint: TemplateConstraintModel,
  field: string
): string | null => {
  const record = constraint as unknown as Record<string, unknown>;
  const value = record[field];
  return typeof value === "string" ? value : null;
};

const extractTemplateUuid = getConstraintTemplateUuid;

const inferItemScope = (
  constraint: TemplateConstraintModel
): ConstraintContextScope => {
  switch (constraint.constraint_type) {
    case "required_bom_item_type":
    case "required_bom_template":
    case "allowed_bom_templates":
    case "aggregate_limit":
      return "bom";
    case "required_process_type":
    case "required_process_template":
    case "allowed_process_templates":
      return "process_requirement";
    default:
      return "item";
  }
};

const buildConstraintContextIndex = (
  constraints: ConstraintsResponse | null | undefined
): Record<string, ConstraintContext> => {
  const contextByUuid: Record<string, ConstraintContext> = {};
  if (!constraints) {
    return contextByUuid;
  }

  const register = (
    constraint: TemplateConstraintModel,
    partial: Partial<ConstraintContext> & { scope?: ConstraintContextScope }
  ) => {
    const key = normalizeKey(constraint.uuid)!;
    contextByUuid[key] = {
      scope: partial.scope ?? "unknown",
      constraint,
      itemUuid: trimValue(partial.itemUuid),
      attributeName: trimValue(partial.attributeName),
      attributeUuid: trimValue(partial.attributeUuid),
      processUuid: trimValue(partial.processUuid),
      processTypeUuid: trimValue(partial.processTypeUuid),
      parameterName: trimValue(partial.parameterName),
      parameterUuid: trimValue(partial.parameterUuid),
      templateUuid: trimValue(partial.templateUuid),
    };
  };

  constraints.item_constraints?.forEach((itemConstraint: ItemConstraints) => {
    const itemUuid = trimValue(itemConstraint.item_uuid);

    Object.entries(itemConstraint.attribute_constraints ?? {}).forEach(
      ([attributeName, attributeConstraints]) => {
        (attributeConstraints ?? []).forEach((constraint) => {
          register(constraint, {
            scope: "attribute",
            itemUuid,
            attributeName,
            attributeUuid: getConstraintString(constraint, "attribute_uuid"),
            templateUuid: extractTemplateUuid(constraint),
          });
        });
      }
    );

    (itemConstraint.process_constraints ?? []).forEach((constraint) => {
      register(constraint, {
        scope: inferItemScope(constraint),
        itemUuid,
        processTypeUuid: getConstraintString(constraint, "process_type_uuid"),
        templateUuid: extractTemplateUuid(constraint),
      });
    });

    (itemConstraint.bom_constraints ?? []).forEach((constraint) => {
      register(constraint, {
        scope: "bom",
        itemUuid,
        templateUuid: extractTemplateUuid(constraint),
      });
    });
  });

  constraints.process_constraints?.forEach(
    (processConstraint: ProcessConstraints) => {
      const processUuid = trimValue(processConstraint.process_uuid);
      Object.entries(processConstraint.parameter_constraints ?? {}).forEach(
        ([parameterName, parameterConstraints]) => {
          (parameterConstraints ?? []).forEach((constraint) => {
            register(constraint, {
              scope: "parameter",
              processUuid,
              parameterName,
              parameterUuid: getConstraintString(constraint, "parameter_uuid"),
              templateUuid: extractTemplateUuid(constraint),
            });
          });
        }
      );
    }
  );

  return contextByUuid;
};

const findProcessIndexByUuid = (
  condition: FormspecCondition,
  bomPath: string,
  processUuid: string | null
): number | null => {
  if (!processUuid) {
    return null;
  }

  const item = getItemAtPath(condition, bomPath);
  if (!item?.processes) {
    return null;
  }

  const index = item.processes.findIndex(
    ({ process }) =>
      normalizeKey(process.uuid ?? null) === normalizeKey(processUuid)
  );

  return index >= 0 ? index : null;
};

export const normalizeServerViolations = (
  violations: ConstraintViolation[],
  condition: FormspecCondition,
  constraints: ConstraintsResponse | null
): ConstraintViolation[] => {
  const baseItemUuid = isItem(condition.item)
    ? condition.item.uuid
    : condition.external_spec_id;
  const basePartNumber = trimValue(condition.item.arena_part_number);
  const contextLookup = buildConstraintContextIndex(constraints);

  return violations.map((violation) => {
    const context =
      contextLookup[normalizeKey(violation.constraint_uuid) ?? ""];
    const merged = {
      ...violation,
    } as ConstraintViolation & ConstraintViolationOptionalFields;

    if (!merged.item_uuid && baseItemUuid) {
      merged.item_uuid = baseItemUuid;
    }

    if (!merged.item_part_number && basePartNumber) {
      merged.item_part_number = basePartNumber;
    }

    merged.bom_path = merged.bom_path
      ? remapServerBomPath(merged.bom_path, condition)
      : "";

    if (context) {
      merged.attribute_name = merged.attribute_name ?? context.attributeName;
      merged.attribute_uuid = merged.attribute_uuid ?? context.attributeUuid;
      merged.parameter_name = merged.parameter_name ?? context.parameterName;
      merged.parameter_uuid = merged.parameter_uuid ?? context.parameterUuid;
      merged.process_uuid = merged.process_uuid ?? context.processUuid;
      merged.process_type_uuid =
        merged.process_type_uuid ?? context.processTypeUuid;
    }

    if (merged.bom_index === undefined || merged.bom_index === null) {
      merged.bom_index = findProcessIndexByUuid(
        condition,
        merged.bom_path ?? "",
        merged.process_uuid ?? null
      );
    }

    return merged;
  });
};

/**
 * Build violation list with context attached
 * Uses simple filtering instead of complex indexes for better code clarity
 */
const buildViolationList = (
  constraintViolations: ConstraintViolation[],
  constraints: ConstraintsResponse | null
): ConstraintViolationWithContext[] => {
  const contextLookup = buildConstraintContextIndex(constraints);

  return constraintViolations.map((violation) => {
    const constraintKey = normalizeKey(violation.constraint_uuid)!;
    const context = contextLookup[constraintKey] ?? null;

    return {
      ...violation,
      context,
    };
  });
};

// ============================================================================
// Formula Dependency Types
// ============================================================================

export type FormulaDependency = {
  targetAttributeName: string;
  targetItemUuid: string;
  targetPath: string;
  formula: string;
  variables: FormulaVariable[];
};

export type FormulaDependencyMap = Record<string, FormulaDependency[]>;

export type AttributeLocation = {
  itemUuid: string;
  attributeName: string;
  path: string;
};

export type AttributeUuidTranslator = Map<string, AttributeLocation[]>;

/**
 * Evaluates a formula expression with given variable values
 */
const evaluateFormula = (
  expression: string,
  variableValues: Record<string, number>
): number | null => {
  try {
    // Replace variable names in the expression with their values
    let evaluableExpression = expression;

    Object.entries(variableValues).forEach(([varName, value]) => {
      // Replace all occurrences of the variable name with its value
      const regex = new RegExp(`\\b${varName}\\b`, "g");
      evaluableExpression = evaluableExpression.replace(regex, String(value));
    });

    // Evaluate the expression using Function constructor (safe in this controlled context)
    // eslint-disable-next-line no-new-func
    const result = new Function(`return ${evaluableExpression}`)();

    return typeof result === "number" && !isNaN(result) ? result : null;
  } catch (error) {
    console.error("Formula evaluation error:", error);
    return null;
  }
};

// ============================================================================
// Violation Management Helpers
// ============================================================================

/**
 * Adds or updates a constraint violation in the validation state.
 * If a violation with the same constraint_type, item_uuid, and bom_path
 * already exists, it updates it. Otherwise, it adds a new violation.
 */
export const upsertViolation = (
  newViolation: ConstraintViolation & Partial<ConstraintViolationOptionalFields>
): void => {
  const state = useValidationStateStore.getState();
  const violations = state.constraintViolations;

  const newKey = getViolationKey(newViolation);
  const existingIndex = violations.findIndex(
    (v) => getViolationKey(v) === newKey
  );

  if (existingIndex === -1) {
    state.setConstraintViolations([...violations, newViolation]);
  } else {
    const updated = [...violations];
    updated[existingIndex] = newViolation;
    state.setConstraintViolations(updated);
  }
};

/**
 * Removes a constraint violation from the validation state.
 * Matches by constraint_type, bom_path, and optionally item_uuid.
 */
export const removeViolation = (
  constraintType: string,
  bomPath: string,
  itemUuid?: string | null
): void => {
  const state = useValidationStateStore.getState();
  const violations = state.constraintViolations;

  const keyToRemove = getViolationKey({
    constraint_type: constraintType,
    item_uuid: itemUuid,
    bom_path: bomPath,
  });

  const filtered = violations.filter((v) => getViolationKey(v) !== keyToRemove);

  if (filtered.length !== violations.length) {
    state.setConstraintViolations(filtered);
  }
};

/**
 * Removes ALL constraint violations at the given bom_path and any
 * descendant paths (paths that start with `bomPath>`).
 * Used when a BOM item is deleted to clear stale violations on
 * that item and its entire subtree.
 */
export const removeViolationsAtAndBelow = (bomPath: string): void => {
  const state = useValidationStateStore.getState();
  const violations = state.constraintViolations;
  const prefix = `${bomPath}>`;

  const filtered = violations.filter((v) => {
    const vPath: string | null | undefined = v.bom_path;
    return vPath !== bomPath && !(vPath || "").startsWith(prefix);
  });

  if (filtered.length !== violations.length) {
    state.setConstraintViolations(filtered);
  }
};

export const getRangeViolationMessage = (
  min: number | undefined,
  max: number | undefined,
  fieldName: string,
  fieldKind: "Attribute" | "Parameter"
): string => {
  if (min !== undefined && max !== undefined) {
    return `${fieldKind} '${fieldName}' must be within [${min}, ${max}]`;
  }
  if (min !== undefined) {
    return `${fieldKind} '${fieldName}' must be at least ${min}`;
  }
  if (max !== undefined) {
    return `${fieldKind} '${fieldName}' must be at most ${max}`;
  }
  return `${fieldKind} '${fieldName}' is out of range`;
};

const coerceRangeNumber = (value: ValueType | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return null;
    }
    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === "number") {
    return value;
  }
  return null;
};

/**
 * Validates a single item at the given path (attributes, parameters,
 * allowed process templates, BOM quantity/template/item-type constraints).
 * Does NOT recurse into BOM children — see runClientSideValidations.
 */
const validateItemAtPath = (
  currentItem: ItemWithBOM,
  path: string,
  constraints: ConstraintsResponse,
  getChange: (target: ChangeTarget) => Change | null
): void => {
  const itemData = currentItem.item;
  if (!("uuid" in itemData) || typeof itemData.uuid !== "string") {
    return;
  }

  const itemUuid = itemData.uuid;
  const attributes = itemData.attributes || {};

  Object.entries(attributes).forEach(([attributeName, attributeValue]) => {
    const constraint = findConstraintOfType(
      getAttributeConstraints(constraints, itemUuid, attributeName),
      "attribute_range"
    );

    if (!constraint) {
      return;
    }

    const rangeConstraint = {
      min: constraint.config.min_value,
      max: constraint.config.max_value,
    };

    const changeTarget = makeAttributeChangeTarget(path, attributeName);
    const change = getChange(changeTarget);
    const currentValue =
      change?.currentValue !== undefined
        ? (change.currentValue as ValueType)
        : attributeValue;

    const numValue = coerceRangeNumber(currentValue);
    if (numValue === null) {
      removeViolation("attribute_range", path, itemUuid);
      return;
    }

    const violatesMin =
      rangeConstraint.min !== undefined && numValue < rangeConstraint.min;
    const violatesMax =
      rangeConstraint.max !== undefined && numValue > rangeConstraint.max;

    if (!violatesMin && !violatesMax) {
      removeViolation("attribute_range", path, itemUuid);
      return;
    }

    upsertViolation({
      constraint_uuid: constraint.uuid,
      constraint_type: "attribute_range",
      bom_path: path,
      item_uuid: itemUuid,
      message: getRangeViolationMessage(
        rangeConstraint.min,
        rangeConstraint.max,
        attributeName,
        "Attribute"
      ),
      attribute_name: attributeName,
    });
  });

  const isProcessDeleted = (index: number) =>
    currentItem.process_changes?.some(
      (change) => change.index === index && change.is_deleted
    ) || false;

  const liveProcesses = collectLiveProcesses(currentItem);

  liveProcesses.forEach(({ processItem, processPath }) => {
    const processIndex = processPath.length === 1 ? processPath[0] : -1;
    const process = processItem.process;
    const processConstraints = constraints.process_constraints.find(
      (pc) => pc.process_uuid === process.uuid
    );
    if (!processConstraints) {
      return;
    }

    Object.entries(processConstraints.parameter_constraints || {}).forEach(
      ([parameterName, parameterConstraints]) => {
        const constraint = findConstraintOfType(
          parameterConstraints ?? [],
          "parameter_range"
        );
        if (!constraint) {
          return;
        }

        const rangeConstraint = {
          min: constraint.config.min_value,
          max: constraint.config.max_value,
        };

        const changeTarget: ChangeTarget = {
          type: "parameter",
          path,
          processPath,
          propertyName: parameterName,
        };
        const change = getChange(changeTarget);

        const baseValue = process.parameters?.[parameterName];

        const currentValue =
          change?.currentValue !== undefined
            ? (change.currentValue as ValueType)
            : baseValue;

        const numValue = coerceRangeNumber(currentValue);
        if (numValue === null) {
          removeViolation("parameter_range", path, itemUuid);
          return;
        }

        const violatesMin =
          rangeConstraint.min !== undefined && numValue < rangeConstraint.min;
        const violatesMax =
          rangeConstraint.max !== undefined && numValue > rangeConstraint.max;

        if (!violatesMin && !violatesMax) {
          removeViolation("parameter_range", path, itemUuid);
          return;
        }

        upsertViolation({
          constraint_uuid: constraint.uuid,
          constraint_type: "parameter_range",
          bom_path: path,
          item_uuid: itemUuid,
          bom_index: processIndex,
          process_uuid: process.uuid,
          parameter_name: parameterName,
          message: getRangeViolationMessage(
            rangeConstraint.min,
            rangeConstraint.max,
            parameterName,
            "Parameter"
          ),
        });
      }
    );
  });

  // --- allowed_process_templates validation ---
  // Collect the union of allowed template UUIDs from all AllowedProcessTemplatesConstraint
  // entries for this item, then check every non-deleted process.
  const itemConstraints = constraints.item_constraints.find(
    (ic) => ic.item_uuid === itemUuid
  );
  const allowedProcessTemplatesConstraints = (
    itemConstraints?.process_constraints || []
  ).filter(
    (c) => c.constraint_type === "allowed_process_templates"
  ) as AllowedProcessTemplatesConstraint[];

  if (allowedProcessTemplatesConstraints.length > 0) {
    // Build union of all allowed template UUIDs
    const allowedTemplateUuids = new Set<string>();
    allowedProcessTemplatesConstraints.forEach((constraint) => {
      constraint.config.allowed_template_uuids?.forEach((uuid) =>
        allowedTemplateUuids.add(uuid)
      );
    });

    // Use the first constraint's UUID as the representative constraint_uuid
    const constraintUuid = allowedProcessTemplatesConstraints[0].uuid;

    let hasViolation = false;
    (currentItem.processes || []).forEach((processItem, processIndex) => {
      if (isProcessDeleted(processIndex)) {
        return;
      }

      const process = processItem.process;
      const templateUuid = process.base_template?.template_definition_uuid;

      if (templateUuid && !allowedTemplateUuids.has(templateUuid)) {
        hasViolation = true;
        upsertViolation({
          constraint_uuid: constraintUuid,
          constraint_type: "allowed_process_templates",
          bom_path: path,
          item_uuid: itemUuid,
          message: `Process '${
            process.process_type?.name || process.name
          }' uses template ${templateUuid} which is not allowed`,
        });
      }
    });

    // If no violations remain, remove the violation for this item/path
    if (!hasViolation) {
      removeViolation("allowed_process_templates", path, itemUuid);
    }
  }

  const itemHasBom = currentItem.bom && currentItem.bom.length > 0;
  if (itemHasBom) {
    const bomItemsWithIndices = currentItem.bom.map((child, index) => ({
      child,
      originalIndex: index,
    }));
    const isBomItemDeleted = (index: number) =>
      currentItem.bom_changes?.some(
        (change) => change.index === index && change.is_deleted
      ) || false;

    bomItemsWithIndices.forEach(({ child, originalIndex }) => {
      if (!isItem(child.item)) {
        return;
      }

      const currentQuantity = getCurrentQuantity(
        child,
        originalIndex,
        path,
        undefined,
        undefined,
        getChange
      );

      validateBomItemQuantity(
        currentQuantity,
        child.item,
        path,
        originalIndex,
        constraints,
        itemUuid,
        bomItemsWithIndices,
        isBomItemDeleted,
        getChange
      );
    });

    validateRequiredBomTemplates(
      constraints,
      itemUuid,
      path,
      bomItemsWithIndices,
      isBomItemDeleted,
      getChange
    );

    validateRequiredBomItemTypes(
      constraints,
      itemUuid,
      path,
      bomItemsWithIndices,
      isBomItemDeleted,
      getChange
    );
  }
};

export const runClientSideValidations = ({
  condition,
  constraints,
  path,
  getChange,
}: {
  condition: FormspecCondition | null;
  constraints: ConstraintsResponse | null;
  path: string;
  getChange: (target: ChangeTarget) => Change | null;
}): void => {
  if (!condition || !constraints) {
    return;
  }

  const currentItem = getItemAtPath(condition, path);
  if (!currentItem) {
    return;
  }

  // Validate the item at this path
  validateItemAtPath(currentItem, path, constraints, getChange);

  // Recurse into BOM children so that every descendant is validated.
  // Children that are pending deletion are skipped — their violations
  // (and those of their descendants) are removed instead.
  if (currentItem.bom) {
    currentItem.bom.forEach((child, index) => {
      const childPath = path ? `${path}>${index}` : `${index}`;

      const isDeleted = (currentItem.bom_changes || []).some(
        (c) => c.index === index && c.is_deleted
      );

      if (isDeleted) {
        removeViolationsAtAndBelow(childPath);
        return;
      }

      runClientSideValidations({
        condition,
        constraints,
        path: childPath,
        getChange,
      });
    });
  }
};

// ============================================================================
// Validation State Store
// ============================================================================

interface ValidationState {
  constraints: ConstraintsResponse | null;
  constraintViolations: ConstraintViolation[];
  formulaDependencies: FormulaDependencyMap;
  attributeUuidTranslator: AttributeUuidTranslator;

  setConstraints: (constraints: ConstraintsResponse | null) => void;
  setConstraintViolations: (
    violations:
      | ConstraintViolation[]
      | ((prev: ConstraintViolation[]) => ConstraintViolation[])
  ) => void;
  setFormulaDependencies: (dependencies: FormulaDependencyMap) => void;
  setAttributeUuidTranslator: (translator: AttributeUuidTranslator) => void;
  evaluateFormulas: (changedTarget: {
    type: "attribute";
    path: string;
    propertyName: string;
  }) => void;
}

export const useValidationStateStore = create<ValidationState>((set, get) => ({
  constraints: null,
  constraintViolations: [],
  formulaDependencies: {},
  attributeUuidTranslator: new Map(),

  setConstraints: (constraints) => set({ constraints }),
  setConstraintViolations: (constraintViolations) =>
    set((state) => {
      const newViolations =
        typeof constraintViolations === "function"
          ? constraintViolations(state.constraintViolations)
          : constraintViolations;

      return { constraintViolations: newViolations };
    }),
  setFormulaDependencies: (formulaDependencies) => set({ formulaDependencies }),
  setAttributeUuidTranslator: (attributeUuidTranslator) =>
    set({ attributeUuidTranslator }),

  evaluateFormulas: (changedTarget) => {
    const state = get();
    const { formulaDependencies } = state;

    // Create the key for the changed attribute
    const sourceKey = `${changedTarget.path}:${changedTarget.propertyName}`;

    // Get all dependent formulas that need to be evaluated
    const dependencies = formulaDependencies[sourceKey];

    if (!dependencies || dependencies.length === 0) {
      return;
    }

    // Get the current condition from specification state
    const specState = useSpecificationStateStore.getState();
    const condition = specState.condition;

    if (!condition) {
      return;
    }

    // Helper to get attribute value from path, checking tracked changes first
    const getAttributeValue = (
      path: string,
      attributeName: string
    ): number | null => {
      // First, check if there's a tracked change for this attribute
      const changeTarget = {
        type: "attribute" as const,
        path: path,
        propertyName: attributeName,
      };

      const trackedChange = specState.getChange(changeTarget);
      if (trackedChange !== null) {
        const value = trackedChange.currentValue;
        return typeof value === "number"
          ? value
          : typeof value === "string"
          ? parseFloat(String(value))
          : null;
      }

      // If no tracked change, get the original value from the item
      const indices = path
        .split(">")
        .filter(Boolean)
        .map((segment) => parseInt(segment, 10));

      // Start from the root - condition itself is an ItemWithBOM
      let currentItem: ItemWithBOM | BOMItem = condition;

      // Navigate through BOM tree
      for (const index of indices) {
        if (!currentItem?.bom || index >= currentItem.bom.length) {
          return null;
        }
        currentItem = currentItem.bom[index];
      }

      // Get the actual item - ItemWithBOM has .item property, BOMItem is the item itself
      const actualItem = "item" in currentItem ? currentItem.item : currentItem;

      if (!actualItem || !("attributes" in actualItem)) {
        return null;
      }

      // attributes is a Record<string, ValueType>
      const attributes = (actualItem as ArenaOnlyItem | Item).attributes;
      if (!attributes || typeof attributes !== "object") {
        return null;
      }

      const value = attributes[attributeName];
      if (value === undefined) {
        return null;
      }

      if (
        typeof value === "object" &&
        value !== null &&
        ("min_value" in value || "max_value" in value)
      ) {
        return null;
      }

      return typeof value === "number"
        ? value
        : typeof value === "string"
        ? parseFloat(String(value))
        : null;
    };

    // Evaluate each dependent formula
    dependencies.forEach((dependency) => {
      // Collect variable values
      const variableValues: Record<string, number> = {};
      let allVariablesAvailable = true;

      for (const variable of dependency.variables) {
        const sourceLocations = state.attributeUuidTranslator.get(
          variable.attribute_uuid
        );
        if (!sourceLocations || sourceLocations.length === 0) {
          allVariablesAvailable = false;
          break;
        }

        // Find the source location co-located with the target (same path)
        // so that each instance resolves its own variables independently.
        const sourceLocation =
          sourceLocations.find((s) => s.path === dependency.targetPath) ||
          sourceLocations[0];

        const value = getAttributeValue(
          sourceLocation.path,
          sourceLocation.attributeName
        );
        if (value === null) {
          allVariablesAvailable = false;
          break;
        }

        variableValues[variable.alias] = value;
      }

      if (!allVariablesAvailable) {
        return;
      }

      // Evaluate the formula
      const calculatedValue = evaluateFormula(
        dependency.formula,
        variableValues
      );

      if (calculatedValue === null) {
        return;
      }

      // Get the current value of the target attribute to use as previousValue
      const currentValue = getAttributeValue(
        dependency.targetPath,
        dependency.targetAttributeName
      );

      // Track the change in the specification state
      specState.trackChange({
        target: {
          type: "attribute",
          path: dependency.targetPath,
          propertyName: dependency.targetAttributeName,
        },
        operation: "update",
        currentValue: calculatedValue,
        previousValue: currentValue !== null ? currentValue : undefined,
        timestamp: Date.now(),
      });
    });
  },
}));

// ============================================================================
// Computed Violation Summary (for non-reactive access)
// ============================================================================

/**
 * Compute full violation summary from violations and constraints
 * Uses simple array filtering instead of complex indexes for better readability
 */
export const computeViolationSummary = (
  constraintViolations: ConstraintViolation[],
  constraints: ConstraintsResponse | null
): ConstraintViolationSummaryResult => {
  const list = buildViolationList(constraintViolations, constraints);

  // Build simple lookup maps
  const contextByConstraintId: Record<string, ConstraintContext> = {};
  const violationsByConstraintId: Record<
    string,
    ConstraintViolationWithContext[]
  > = {};

  list.forEach((v) => {
    if (v.context) {
      const key = normalizeKey(v.constraint_uuid)!;
      contextByConstraintId[key] = v.context;

      if (!violationsByConstraintId[key]) {
        violationsByConstraintId[key] = [];
      }
      violationsByConstraintId[key].push(v);
    }
  });

  return {
    list,
    hasViolations: list.length > 0,
    contextByConstraintId,
    violationsByConstraintId,

    // Simple filtering functions - performance is fine for typical violation counts
    getItemViolationsByPath: (bomPath: string) => {
      const pathKey = normalizeKey(bomPath);
      if (pathKey === null) {
        return list.filter((v) => !v.bom_path || v.bom_path === "");
      }
      return list.filter((v) => normalizeKey(v.bom_path ?? null) === pathKey);
    },

    getAttributeViolationsByPath: (
      bomPath: string,
      attributeName: string | null | undefined
    ) => {
      const attributeKey = normalizeKey(attributeName);
      if (!attributeKey) {
        return [];
      }
      const pathKey = normalizeKey(bomPath);

      return list.filter((v) => {
        const vPath = normalizeKey(v.bom_path ?? null);
        const vAttr = normalizeKey(
          v.attribute_name ?? v.context?.attributeName ?? null
        );

        if (pathKey === null) {
          return (!v.bom_path || v.bom_path === "") && vAttr === attributeKey;
        }
        return vPath === pathKey && vAttr === attributeKey;
      });
    },

    getProcessViolationsByPath: (
      bomPath: string,
      processIndex: number,
      processUuid?: string | null
    ) => {
      const pathKey = normalizeKey(bomPath);
      const processKey = normalizeKey(processUuid);

      return list.filter((v) => {
        const vPath = normalizeKey(v.bom_path ?? null);
        const violationProcessKey = normalizeKey(
          v.process_uuid ?? v.context?.processUuid ?? null
        );
        const matchesProcess =
          v.bom_index === processIndex ||
          Boolean(processKey && violationProcessKey === processKey);

        if (pathKey === null) {
          return (
            (!v.bom_path || v.bom_path === "") &&
            matchesProcess &&
            Boolean(violationProcessKey)
          );
        }
        return (
          vPath === pathKey && matchesProcess && Boolean(violationProcessKey)
        );
      });
    },

    getParameterViolationsByPath: (
      bomPath: string,
      processIndex: number,
      parameterName: string | null | undefined,
      processUuid?: string | null
    ) => {
      const parameterKey = normalizeKey(parameterName);
      if (!parameterKey) {
        return [];
      }
      const pathKey = normalizeKey(bomPath);
      const processKey = normalizeKey(processUuid);

      return list.filter((v) => {
        const vPath = normalizeKey(v.bom_path ?? null);
        const vParam = normalizeKey(
          v.parameter_name ?? v.context?.parameterName ?? null
        );
        const violationProcessKey = normalizeKey(
          v.process_uuid ?? v.context?.processUuid ?? null
        );
        // Steps are reported with bom_index -1, so a negative index matches
        // by uuid only; otherwise every step's violation would match.
        const matchesProcess =
          (processIndex >= 0 && v.bom_index === processIndex) ||
          Boolean(processKey && violationProcessKey === processKey);

        if (pathKey === null) {
          return (
            (!v.bom_path || v.bom_path === "") &&
            matchesProcess &&
            vParam === parameterKey
          );
        }
        return vPath === pathKey && matchesProcess && vParam === parameterKey;
      });
    },

    hasViolationsAtOrBelow: (bomPath: string) => {
      const pathKey = normalizeKey(bomPath);
      // Root node — any violation counts
      if (pathKey === null) {
        return list.length > 0;
      }
      const prefix = pathKey + ">";
      return list.some((v) => {
        const vPath = normalizeKey(v.bom_path ?? null);
        return (
          vPath === pathKey || (vPath !== null && vPath.startsWith(prefix))
        );
      });
    },
  };
};

/**
 * Hook to reactively subscribe to violation summary
 * Use this in components that need to re-render when violations change
 */
export const useViolationSummary = (): ConstraintViolationSummaryResult => {
  // Force subscription to trigger on any violation array change
  const violationsRef = useValidationStateStore(
    (state) => state.constraintViolations
  );
  const constraints = useValidationStateStore((state) => state.constraints);

  // Use useMemo with explicit dependencies to recompute when violations change
  return useMemo(
    () => computeViolationSummary(violationsRef, constraints),
    [violationsRef, constraints]
  );
};
